import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import PrintIcon from "@mui/icons-material/Print";

import { ListItemButton } from "../drawerUtils";
import { useMount } from "constants/constants";
import qs from "query-string";
import {
  useEffectOnce,
  useFocusRange,
  useGetPageFromPath,
  useSelectedShift,
} from "utils/customHooks/genericHooks";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  useIsStillLoadingDataToStore,
  useIsTrendChartStillLoading,
} from "pages/DashboardPages/LoadingIndicatorDashboardPages";
import { useMemo } from "react";

const PrintOrDownloadButton = ({ setIsRightDrawerOpen }) => {
  const focusRange = useFocusRange();
  const selectedShift = useSelectedShift();
  const { isHubletPage } = useGetPageFromPath();
  const { translate } = useFlightTranslation();

  // when we print, hide obstructive elements which are generated outside of the root div
  useMount(() => {
    const obstructiveElementsOutsideOfApp = document.querySelectorAll(
      `.MuiBackdrop-root,
  .MuiDrawer-paper`
    );
    const previousZoom = (document.body.style as any).zoom; // zoom is non-standard, some browsers only
    window.onbeforeprint = () => {
      // hide obstructive elements
      obstructiveElementsOutsideOfApp.forEach(
        el => ((el as HTMLElement).style.display = "none")
      );

      // reset browser zoom level (works in Chrome, IE)
      (document.body.style as any).zoom = "100%";
    };
    window.onafterprint = () => {
      // revert browser zoom level to previous
      (document.body.style as any).zoom = previousZoom;

      // close the drawer after printing

      // since a simple setTimeout(window.print,2000) after closing the drawer doesn't work
      // (on Chrome, the interface becomes unclickable afterwards for some reason)
      // instead we'll close the drawer,
      setIsRightDrawerOpen(false);
      // then wait for it to finish closing before un-hiding it (otherwise it looks twitchy)
      setTimeout(() => {
        obstructiveElementsOutsideOfApp.forEach(
          el => ((el as HTMLElement).style.display = "")
        );
      }, 2000);
    };
  });

  return (
    <ListItemButton
      onClick={() => {
        // window.print refreshes the page, so we always get taken to the current shift
        // (refreshes on Hublet page only? but on Timeline page it just works with Ctrl+P?? but WHY)
        const isCurrentShift = selectedShift?.range?.lt > Date.now();
        const canPrintImmediately = !isHubletPage || isCurrentShift;
        if (canPrintImmediately) {
          window.print();
        } else {
          // otherwise, to stay on the time range we're looking at,
          // add ge & lt query params to the url, and open in a new tab.
          // also add "print" query param to print when it opens.
          const query = qs.parse(window.location.search);
          const newQuery = {
            ...query,
            ...(focusRange ? { ge: focusRange.ge, lt: focusRange.lt } : {}),
            printAndClose: true,
          };
          const newQueryString = qs.stringify(newQuery);

          window.open(
            `${window.location.origin}${window.location.pathname}${
              newQueryString ? "?" + newQueryString : ""
            }`
          );
        }
      }}
      aria-label="Print / Download"
    >
      <ListItemIcon>
        <PrintIcon />
      </ListItemIcon>
      <ListItemText primary={translate("Print_/_Download")} />
    </ListItemButton>
  );
};

export const SHOULD_PRINT =
  window.location.search.includes("printAndClose=true");
/** when we need to print a past timerange, the page opens in a new tab with "printAndClose=true" + ge & lt query params,
 * then prints when it's done loading, then closes after printing.
 */
export const usePrintOnMount = () => {
  const isDataLoading = useIsStillLoadingDataToStore();
  const { isLoading: isTrendChartLoading } = useIsTrendChartStillLoading();
  const shouldPrintAndClose = useMemo(() => {
    const readyToPrint = !isDataLoading && !isTrendChartLoading;
    const shouldPrintAndClose = SHOULD_PRINT && readyToPrint;
    return shouldPrintAndClose;
  }, [isDataLoading, isTrendChartLoading]);

  useEffectOnce({
    callback: () => {
      window.setTimeout(() => {
        // close the tab after printing is complete
        window.onafterprint = () => {
          window.close();
        };
        window.print();
      }, 5 * 1000);
    },
    shouldRun: shouldPrintAndClose,
    dependencies: [shouldPrintAndClose],
  });
};

export default PrintOrDownloadButton;
